import z from 'zod';
import type { TemplateFormField, TemplateFormFieldInsert } from '../../backend/src/schema';
import type { JSONCompatible } from './index';
import { HiddenContactFieldsSchema, RequiredContactFieldsSchema } from './templateForms';

export type { TemplateFormField, TemplateFormFieldInsert };

const NextQuestionSchema = z.object({
    name: z.string(),
});

const ConditionSchema = z.object({
    value: z.string(),
    nextQuestion: NextQuestionSchema.optional(),
});
export type Condition = z.infer<typeof ConditionSchema>;

const OptionSchema = z.object({
    optionLabel: z.string().optional(),
    optionValue: z.union([z.number(), z.string()]).optional(),
    isNewAdded: z.boolean().optional(),
});
export type QuestionOption = z.infer<typeof OptionSchema>;

const SelectOptionSchema = z.object({
    selectItemKey: z.union([z.number(), z.string()]).optional(),
    selectItemLabel: z.string().optional(),
    selectItemValue: z.union([z.number(), z.string()]).optional(),
    isNewAdded: z.boolean().optional(),
    isSelectItemDisabled: z.boolean().optional(),
});
export type SelectOption = z.infer<typeof SelectOptionSchema>;

const TableColumnSelectOptionSchema = z.object({
    selectItemLabel: z.string(),
    selectItemValue: z.string(),
    selectItemKey: z.string().optional(),
});

export const TABLE_COLUMN_TYPES = [
    'text',
    'textarea',
    'select',
    'currency',
    'percentage',
    'date',
    'address',
    'upload',
    'multiselect',
] as const;
export type TableColumnType = (typeof TABLE_COLUMN_TYPES)[number];

export const CURRENCY_VALUE_TYPES = ['asset', 'debt'] as const;
export type CurrencyValueType = (typeof CURRENCY_VALUE_TYPES)[number];

/** How many files a new upload question or upload table column accepts, and the range admins can set */
export const DEFAULT_UPLOAD_FILE_COUNT = 3;
export const MIN_UPLOAD_FILE_COUNT = 1;
export const MAX_UPLOAD_FILE_COUNT = 20;
/** Upload table columns saved before the count was configurable accepted 5 files */
export const LEGACY_TABLE_UPLOAD_FILE_COUNT = 5;

export const TableFormColumnSchema = z.object({
    key: z.string(),
    label: z.string(),
    type: z.enum(TABLE_COLUMN_TYPES),
    valueType: z.enum(CURRENCY_VALUE_TYPES).optional(),
    typeOfDocument: z.string().optional(),
    fileCount: z.number().optional(),
    width: z.union([z.string(), z.number()]).optional(),
    placeholder: z.string().optional(),
    selectOptions: z.array(TableColumnSelectOptionSchema).optional(),
    options: z.array(OptionSchema).optional(),
});
export type TableFormColumn = z.infer<typeof TableFormColumnSchema>;

export const QUESTION_TYPES = [
    'address',
    'currency',
    'date',
    'multiselect',
    'number',
    'percentage',
    'radio',
    'select',
    'table',
    'text',
    'textarea',
    'upload',
    'contactInfoSection',
] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

/** Question types that cannot be placed inside a contact block. */
export const NON_NESTABLE_QUESTION_TYPES: readonly QuestionType[] = ['contactInfoSection'];

export const QuestionValidationSchemaSchema = z.object({
    onChange: z
        .object({
            fileSize: z.number().optional(),
            fileType: z.array(z.string()).optional(),
            fileCount: z.number().optional(),
            notEqual: z.enum(['firstIdType', 'secondIdType']).optional(),
            type: z.enum(['date', 'email']).optional(),
            max: z.number().optional(),
            required: z.boolean().optional(),
        })
        .optional(),
    required: z.boolean().optional(),
    onchange: z
        .object({
            max: z.number(),
        })
        .optional(),
});
export type QuestionValidationSchema = z.infer<typeof QuestionValidationSchemaSchema>;

export const QuestionSchemaSchema = z.object({
    name: z.string(),
    type: z.enum(QUESTION_TYPES),
    label: z.string().optional(),
    order: z.number().optional(),
    value: z.custom<JSONCompatible>().optional(),
    valueType: z.enum(CURRENCY_VALUE_TYPES).optional(),
    columns: z.array(TableFormColumnSchema).optional(),
    options: z.array(OptionSchema).optional(),
    conditions: z.array(ConditionSchema).optional(),
    description: z.union([z.string(), z.array(z.string())]).optional(),
    formGroupTitle: z.string().nullable().optional(),
    radioOrientation: z.enum(['horizontal', 'vertical']).optional(),
    placeholder: z.string().optional(),
    helper: z.string().optional(),
    isError: z.boolean().optional(),
    isCustomQuestion: z.boolean().optional(),
    note: z.string().optional(),
    conditionedBy: z.string().optional(),
    conditionValue: z.union([z.string(), z.number()]).optional(),
    isConditionalQuestion: z.boolean().optional(),
    typeOfDocument: z.string().optional(),
    selectOptions: z.array(SelectOptionSchema).optional(),
    restrictOptionEdit: z.boolean().optional(),
    isOtherTextInputEnable: z.boolean().optional(),
    lang: z.string().optional(),
    isRequiredForFullAutomation: z.boolean().optional(),
    tags: z.array(z.string()).optional(),
    rowCount: z.number().int().positive().nullable().optional(),
    get contactBlock(): z.ZodOptional<typeof ContactBlockConfigSchema> {
        return ContactBlockConfigSchema.optional();
    },
});
export type QuestionSchema = z.infer<typeof QuestionSchemaSchema>;

export const OptionalCoreContactFieldsSchema = z.object({
    email: z.boolean().optional(),
    cellPhone: z.boolean().optional(),
});
export type OptionalCoreContactFields = z.infer<typeof OptionalCoreContactFieldsSchema>;

/**
 * Contact blocks can also hide email and cell phone. The main contact section can't, as it
 * needs them to reach the client.
 */
export const ContactBlockHiddenFieldsSchema = HiddenContactFieldsSchema.extend(
    OptionalCoreContactFieldsSchema.shape,
);
export type ContactBlockHiddenFields = z.infer<typeof ContactBlockHiddenFieldsSchema>;

/**
 * Configuration for a `contactInfoSection` question: a repeatable contact block
 * (e.g. directors, shareholders, officers) with optional nested questions per entry.
 */
export const ContactBlockConfigSchema = z.object({
    entryLabel: z.string(),
    /** Entries have no contact fields at all, only the nested questions (a custom-fields-only block). */
    hideContactFields: z.boolean().optional(),
    hiddenFields: ContactBlockHiddenFieldsSchema.optional(),
    requiredFields: RequiredContactFieldsSchema.optional(),
    optionalCoreFields: OptionalCoreContactFieldsSchema.optional(),
    description: z.string().optional(),
    /** Most entries a client can add. Unset means no limit. */
    maxEntries: z.number().int().positive().optional(),
    get questions(): z.ZodOptional<z.ZodArray<typeof BlockQuestionSchema>> {
        return z.array(BlockQuestionSchema).optional();
    },
});
export type ContactBlockConfig = z.infer<typeof ContactBlockConfigSchema>;

export const TemplateFormFieldUpdateSchema = z.object({
    templateFormFieldId: z.guid().optional(),
    name: z.string(),
    title: z.string(),
    subTitle: z.string().nullable().optional(),
    questionSchema: QuestionSchemaSchema,
    questionValidationSchema: QuestionValidationSchemaSchema.nullable().optional(),
    displayOrder: z.number().optional(),
    isVisibleToUser: z.boolean().optional(),
    isLocked: z.boolean().nullable().optional(),
    formGroupTitle: z.string().nullable().optional(),
    questionId: z.number().nullable().optional(),
    isRequiredForFullAutomation: z.boolean().optional(),
    isCriticalQuestion: z.boolean().optional(),
});
export type TemplateFormFieldUpdate = z.infer<typeof TemplateFormFieldUpdateSchema>;
export type TemplateFormFieldPayload = TemplateFormFieldUpdate;

/**
 * A question nested inside a contact block. Same shape as a top-level template form field
 * (minus row-level identity), so every question feature works inside blocks.
 */
export const BlockQuestionSchema = TemplateFormFieldUpdateSchema.pick({
    name: true,
    title: true,
    subTitle: true,
    questionSchema: true,
    questionValidationSchema: true,
    isVisibleToUser: true,
});
export type BlockQuestion = z.infer<typeof BlockQuestionSchema>;

export type TemplateFormFieldInsertOmitFormId = Omit<TemplateFormFieldInsert, 'templateFormId'>;

export function toTemplateFormFieldPayload(field: TemplateFormField): TemplateFormFieldPayload {
    return {
        templateFormFieldId: field.templateFormFieldId,
        name: field.name,
        title: field.title,
        subTitle: field.subTitle,
        questionSchema: field.questionSchema,
        questionValidationSchema: field.questionValidationSchema,
        displayOrder: field.displayOrder,
        isVisibleToUser: field.isVisibleToUser,
        isLocked: field.isLocked,
        formGroupTitle: field.formGroupTitle,
        questionId: field.questionId,
        isRequiredForFullAutomation: field.isRequiredForFullAutomation,
        isCriticalQuestion: field.isCriticalQuestion,
    };
}
